// @ts-nocheck
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Selector, SelectorOptionPropsInterface } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { ContextualTargeting } from 'sonata-static-storage/lib/schemas/campaign/targeting/contextualTargeting.schema';

import { reportingInsightsSetGroupByConfig } from '@/store/actions/reporting.actions';
import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';

import './contentCategoryProvider.scss';

type Props = {
  disallow?: ProviderItem['value'][];
  field: string;
  insightId: string;
  onFilter: (optionSelected: { value: string }) => void;
};

type ProviderItem = {
  id: string;
  label: string;
  value: 'iabV1' | 'iabV2' | 'iasV1';
};

const PROVIDER_OPTIONS: Record<string, ProviderItem[]> = {
  'enrichments.appCategoriesByVersion': [
    {
      id: 'enrichments.appCategoriesByVersion.iabV1',
      label: 'reporting.insights.customComponents.contentCategoryProvider.providerOptions.iabV1',
      value: 'iabV1'
    },
    {
      id: 'enrichments.appCategoriesByVersion.iabV2',
      label: 'reporting.insights.customComponents.contentCategoryProvider.providerOptions.iabV2',
      value: 'iabV2'
    },
    {
      id: 'thirdPartyCategories.ias',
      label: 'reporting.insights.customComponents.contentCategoryProvider.providerOptions.iasV1',
      value: 'iasV1'
    }
  ],
  'enrichments.contentCategoriesByVersion': [
    {
      id: 'enrichments.contentCategoriesByVersion.iabV1',
      label: 'reporting.insights.customComponents.contentCategoryProvider.providerOptions.iabV1',
      value: 'iabV1'
    },
    {
      id: 'enrichments.contentCategoriesByVersion.iabV2',
      label: 'reporting.insights.customComponents.contentCategoryProvider.providerOptions.iabV2',
      value: 'iabV2'
    },
    {
      id: 'thirdPartyCategories.ias',
      label: 'reporting.insights.customComponents.contentCategoryProvider.providerOptions.iasV1',
      value: 'iasV1'
    }
  ]
};

const ContentCategoryProvider: React.VFC<Props> = ({ disallow = [], field, insightId, onFilter }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const campaignIds: string[] = useSelector(getCampaigns);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const reportingCampaigns: {
    contextualTargeting: ContextualTargeting;
    id: string;
  }[] = useSelector(getSelectedCampaigns);
  const campaigns = reportingCampaigns.filter(({ id }) => campaignIds.includes(id));

  const providerPermissions: Record<string, boolean> = {
    iabV1:
      checkUserResourcePermissions('reportingFilterEnrichmentsAppCategoriesByVersionIabV1', user, userRoles) ||
      checkUserResourcePermissions('reportingFilterEnrichmentsContentCategoriesByVersionIabV1', user, userRoles),
    iabV2:
      checkUserResourcePermissions('reportingFilterEnrichmentsAppCategoriesByVersionIabV2', user, userRoles) ||
      checkUserResourcePermissions('reportingFilterEnrichmentsContentCategoriesByVersionIabV2', user, userRoles),
    iasV1: checkUserResourcePermissions('reportingFilterContentCategoriesIAS', user, userRoles)
  };

  const providersEnabled = PROVIDER_OPTIONS[field].filter(p =>
    campaigns.some(({ contextualTargeting }) => {
      const key = `${p.value.startsWith('iab') ? 'iab' : 'ias'}Categories` as const;
      return (
        (key.includes('iabCategories') || contextualTargeting[key].enabled) &&
        providerPermissions[p.value] &&
        !disallow.includes(p.value)
      );
    })
  );

  const formattedProviderOptions = providersEnabled.map(o => ({
    ...o,
    label: formatMessage({
      defaultMessage: o.value,
      id: o.label
    })
  }));

  const handleProviderChange = (option: SelectorOptionPropsInterface): void => {
    onFilter({ value: option.id as string });
    dispatch(
      reportingInsightsSetGroupByConfig({
        insightGroupByConfig: { groupBy: option },
        insightId
      })
    );
  };

  const filterPermission: Record<string, boolean> = {
    'enrichments.appCategoriesByVersion': checkUserResourcePermissions(
      'reportingFilterEnrichmentsAppCategoriesByVersion',
      user,
      userRoles
    ),
    'enrichments.contentCategoriesByVersion': checkUserResourcePermissions(
      'reportingFilterEnrichmentsContentCategoriesByVersion',
      user,
      userRoles
    )
  };

  const hasPermissionToFilter = filterPermission[field];

  return hasPermissionToFilter ? (
    <div className="contentCategoryProvider">
      <Selector
        className="contentCategoryProvider__selector"
        defaultValue={[formattedProviderOptions[0]]}
        isDisabled={formattedProviderOptions.length === 1}
        label={formatMessage({
          defaultMessage: 'Provider',
          id: 'reporting.insights.customComponents.contentCategoryProvider.label'
        })}
        onChange={handleProviderChange}
        options={formattedProviderOptions}
        type="simple"
      />
    </div>
  ) : null;
};

export default ContentCategoryProvider;
